// Thin wrapper over the admin-only endpoints. Keeping these in one module
// makes it trivial to (a) add cross-cutting concerns like toast-on-error,
// (b) swap the underlying transport, (c) audit which surfaces hit which
// admin route.
import { api } from './api';

// ---------- helpers ----------

/**
 * Build the standard params block for list calls.
 *
 * `archived` true → backend returns *all* rows (active + soft-deleted) via
 * `?include_deleted=true`. The caller is responsible for filtering down to
 * only deleted rows when displaying an "Archived" view.
 */
const listParams = (params = {}) => {
  const { archived, ...rest } = params;
  return { limit: 200, ...(archived ? { include_deleted: true } : {}), ...rest };
};

const wrapList = async (path, params) => {
  const { data, headers } = await api.get(path, { params: listParams(params) });
  return {
    items: Array.isArray(data) ? data : [],
    total: parseInt(headers?.['x-total-count'] ?? (Array.isArray(data) ? data.length : 0), 10),
  };
};

// ---------- Organizations (super-admin only) ----------

export const adminListOrgs       = (params)  => wrapList('/organizations', params);
export const adminCreateOrg      = (body)    => api.post('/organizations', body).then((r) => r.data);
export const adminUpdateOrg      = (id, body)=> api.patch(`/organizations/${id}`, body).then((r) => r.data);
export const adminDeleteOrg      = (id)      => api.delete(`/organizations/${id}`).then((r) => r.data);
export const adminRestoreOrg     = (id)      => api.post(`/organizations/${id}/restore`).then((r) => r.data);
export const adminHardDeleteOrg  = (id)      => api.delete(`/organizations/${id}`, { params: { hard: true } }).then((r) => r.data);

// ---------- Users ----------

export const adminListUsers        = (params)  => wrapList('/users', params);
export const adminGetUser          = (id)      => api.get(`/users/${id}`).then((r) => r.data);
export const adminCreateUser       = (body)    => api.post('/users', body).then((r) => r.data);
export const adminUpdateUser       = (id, body)=> api.patch(`/users/${id}`, body).then((r) => r.data);
export const adminDeleteUser       = (id)      => api.delete(`/users/${id}`).then((r) => r.data);
export const adminRestoreUser      = (id)      => api.post(`/users/${id}/restore`).then((r) => r.data);
export const adminHardDeleteUser   = (id)      => api.delete(`/users/${id}`, { params: { hard: true } }).then((r) => r.data);

// Super-admin: list a user's org memberships, add/remove.
export const adminListUserOrgs = (userId) =>
  api.get(`/users/${userId}/organizations`).then((r) => Array.isArray(r.data) ? r.data : []);
export const adminAssignUserToOrg = (orgId, body) =>
  api.post(`/organizations/${orgId}/members`, body).then((r) => r.data);
export const adminRemoveUserFromOrg = (orgId, userId) =>
  api.delete(`/organizations/${orgId}/members/${userId}`).then((r) => r.data);

// ---------- Rooms ----------

export const adminListRooms        = (params)  => wrapList('/rooms', params);
export const adminCreateRoom       = (body)    => api.post('/rooms', body).then((r) => r.data);
export const adminUpdateRoom       = (id, body)=> api.patch(`/rooms/${id}`, body).then((r) => r.data);
export const adminDeleteRoom       = (id)      => api.delete(`/rooms/${id}`).then((r) => r.data);
export const adminRestoreRoom      = (id)      => api.post(`/rooms/${id}/restore`).then((r) => r.data);
export const adminHardDeleteRoom   = (id)      => api.delete(`/rooms/${id}`, { params: { hard: true } }).then((r) => r.data);

// ---------- Patients ----------

export const adminListPatients       = (params)  => wrapList('/patients', params);
export const adminCreatePatient      = (body)    => api.post('/patients', body).then((r) => r.data);
export const adminUpdatePatient      = (id, body)=> api.patch(`/patients/${id}`, body).then((r) => r.data);
export const adminDeletePatient      = (id)      => api.delete(`/patients/${id}`).then((r) => r.data);
export const adminRestorePatient     = (id)      => api.post(`/patients/${id}/restore`).then((r) => r.data);
export const adminHardDeletePatient  = (id)      => api.delete(`/patients/${id}`, { params: { hard: true } }).then((r) => r.data);

// ---------- Devices ----------
// Optional `orgId` sets a per-request X-Org-Id override so a super-admin can
// act on a device that lives in a DIFFERENT org than their active one (the
// cross-org "Full list" view). The interceptor respects an explicit override.
const orgHeader = (orgId) => (orgId ? { headers: { 'X-Org-Id': orgId } } : {});

export const adminListDevices       = (params)          => wrapList('/devices', params);
export const adminCreateDevice      = (body, orgId)     => api.post('/devices', body, orgHeader(orgId)).then((r) => r.data);
export const adminUpdateDevice      = (id, body, orgId) => api.patch(`/devices/${id}`, body, orgHeader(orgId)).then((r) => r.data);
export const adminDeleteDevice      = (id, orgId)       => api.delete(`/devices/${id}`, orgHeader(orgId)).then((r) => r.data);
export const adminRestoreDevice     = (id, orgId)       => api.post(`/devices/${id}/restore`, null, orgHeader(orgId)).then((r) => r.data);
export const adminHardDeleteDevice  = (id, orgId)       => api.delete(`/devices/${id}`, { params: { hard: true }, ...orgHeader(orgId) }).then((r) => r.data);

// Cross-org (super-admin): every device across all tenants. NOT X-Org-Id scoped.
// GET /api/admin/devices?q&assignment&organization_id&status&page&page_size
//   → { items, total, page, page_size }. Each item carries organization_id /
//     organization_name and assigned_room_name / assigned_patient_name.
export const adminListAllDevices = async (params = {}) => {
  const { q, assignment, organization_id, status, type, page = 1, page_size = 25 } = params;
  const query = { page, page_size };
  if (q) query.q = q;
  if (assignment && assignment !== 'all') query.assignment = assignment;
  if (organization_id) query.organization_id = organization_id;
  if (status && status !== 'all') query.status = status;
  if (type && type !== 'all') query.type = type;
  const { data } = await api.get('/admin/devices', { params: query });
  return {
    items: Array.isArray(data?.items) ? data.items : [],
    total: Number.isFinite(data?.total) ? data.total : 0,
    page: data?.page ?? page,
    page_size: data?.page_size ?? page_size,
  };
};

// ---------- Device types (catalog) ----------
// GET /api/device-types (any authenticated user, X-Org-Id required — the api
// interceptor injects the active org automatically) → { device_types: [...] }.
export const adminListDeviceTypes = () =>
  api.get('/device-types').then((r) => (Array.isArray(r.data?.device_types) ? r.data.device_types : []));

// PATCH /api/admin/device-types/:type/label (super-admin only, no X-Org-Id).
// Renames the display label only — internal key / hub mapping / sync behaviour
// are untouched. → { type_value, label }.
export const adminUpdateDeviceTypeLabel = (type, label) =>
  api.patch(`/admin/device-types/${encodeURIComponent(type)}/label`, { label }).then((r) => r.data);

// Rooms / patients for a SPECIFIC org (transfer target) via X-Org-Id override.
export const adminListRoomsForOrg = (orgId) =>
  api.get('/rooms', { params: { limit: 200 }, ...orgHeader(orgId) }).then((r) => (Array.isArray(r.data) ? r.data : []));
export const adminListPatientsForOrg = (orgId) =>
  api.get('/patients', { params: { limit: 200 }, ...orgHeader(orgId) }).then((r) => (Array.isArray(r.data) ? r.data : []));

// ---------- Buildings (corpus) ----------

export const adminListBuildings       = (params)  => wrapList('/corpus', params);
export const adminCreateBuilding      = (body)    => api.post('/corpus', body).then((r) => r.data);
export const adminUpdateBuilding      = (id, body)=> api.patch(`/corpus/${id}`, body).then((r) => r.data);
export const adminDeleteBuilding      = (id)      => api.delete(`/corpus/${id}`).then((r) => r.data);
export const adminRestoreBuilding     = (id)      => api.post(`/corpus/${id}/restore`).then((r) => r.data);
export const adminHardDeleteBuilding  = (id)      => api.delete(`/corpus/${id}`, { params: { hard: true } }).then((r) => r.data);

// ---------- Alert policy (org-scoped) ----------
// Backend route is org-scoped so we don't depend on the X-Org-Id header
// for these calls — keeps the surface explicit for super-admins switching
// between tenants from the same browser tab.

export const adminGetAlertPolicy    = (orgId)       => api.get(`/organizations/${orgId}/alert-policy`).then((r) => r.data);
export const adminUpdateAlertPolicy = (orgId, body) => api.put(`/organizations/${orgId}/alert-policy`, body).then((r) => r.data);
export const adminAckAllOrgAlerts   = (orgId)       => api.post(`/organizations/${orgId}/alerts/ack-all`).then((r) => r.data);

// ---------- Sensor thresholds (org-scoped) ----------
// H4 Pro temperature/humidity alert boundaries. All four fields optional;
// send null to clear a boundary. GET returns the current values (may be null).
export const adminGetSensorThresholds    = (orgId)       => api.get(`/organizations/${orgId}/sensor-thresholds`).then((r) => r.data);
export const adminUpdateSensorThresholds = (orgId, body) => api.put(`/organizations/${orgId}/sensor-thresholds`, body).then((r) => r.data);

// ---------- Gateways (super-admin only, read-only) ----------
export const adminListGateways = () => api.get('/gateways').then((r) => r.data || {});

// ---------- QA / test injectors ----------
// These are optional and only used by the Administration "Test tools" tab.
// Backend should gate them behind admin role + a feature flag in production.

export const adminInjectEvent = (body) => api.post('/dev/events', body).then((r) => r.data);
export const adminInjectAlert = (body) => api.post('/dev/alerts', body).then((r) => r.data);

// ---------- shared ----------

// Best-effort error formatter shared by every admin page's toast.
export function formatAdminError(action, err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  const text = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map((d) => d?.msg || String(d)).join('; ') : err?.message);
  if (status === 404) return `${action}: endpoint not available on this backend yet (404).`;
  if (status === 401 || status === 403) return `${action}: not authorised.`;
  if (status === 409) return `${action}: conflict (already exists?). ${text || ''}`.trim();
  if (status === 422 || status === 400) return `${action}: ${text || 'validation failed'}`;
  return `${action} failed${status ? ` (HTTP ${status})` : ''}${text ? `: ${text}` : ''}`;
}
